import { useDepartmentText } from 'model/DepartmentTerminology';
import { Menu, ScrollArea } from '@mantine/core';
import P4Medium from 'components/typography/P4Medium';
import { noop } from 'lodash';
import { useFormInputs } from 'pages/revisions-module/template-editor/editors/form/input/FormInputsDataProvider';
import {
  APPROVER_PROPERTY_PLACEHOLDERS,
  ASSIGNER_ORGANIZATION_PROPERTY_PLACEHOLDERS,
  CONFIRMER_PROPERTY_PLACEHOLDERS,
  DEPARTMENT_PROPERTY_PLACEHOLDERS,
  DEVICE_PROPERTY_PLACEHOLDERS,
  DOCUMENT_PROPERTY_PLACEHOLDERS,
  getPlaceholderTypeLabel,
  IPlaceholder,
  IPlaceholderContext,
  ORGANIZATION_PROPERTY_PLACEHOLDERS,
  REVISION_PROPERTY_PLACEHOLDERS,
  TECHNICIAN_ORGANIZATION_PROPERTY_PLACEHOLDERS,
  TECHNICIAN_PROPERTY_PLACEHOLDERS,
} from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/Placeholder';
import { Fragment, useMemo } from 'react';
import { usePipelineData } from 'pages/revisions-module/template-editor/editors/pipeline/PipelineDataProvider';
import { useDepartmentPlaceholders } from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/department-placeholders';

interface PlaceholderMenuProps {
  onClick?: (placeholder: IPlaceholder) => void;
  excludeSpecialTechnicianProperties?: boolean;
  excludeSpecialApproverProperties?: boolean;
  excludeSpecialConfirmerProperties?: boolean;
  excludeSpecialRevisionProperties?: boolean;
  excludeSpecialOrganizationProperties?: boolean;
  excludeSpecialTechnicianOrganizationProperties?: boolean;
  excludeSpecialAssignerOrganizationProperties?: boolean;
  excludeSpecialDepartmentProperties?: boolean;
  excludeSpecialDocumentProperties?: boolean;
}

/**
 * Menu for displaying placeholders.
 */
export default function PlaceholderMenu({
  onClick = noop,
  excludeSpecialTechnicianProperties = false,
  excludeSpecialApproverProperties = false,
  excludeSpecialConfirmerProperties = false,
  excludeSpecialRevisionProperties = false,
  excludeSpecialOrganizationProperties = false,
  excludeSpecialTechnicianOrganizationProperties = false,
  excludeSpecialAssignerOrganizationProperties = false,
  excludeSpecialDepartmentProperties = false,
  excludeSpecialDocumentProperties = false,
}: PlaceholderMenuProps) {
  const departmentText = useDepartmentText();
  const departmentPlaceholders = useDepartmentPlaceholders();
  const { allInputs } = useFormInputs();
  const { placeholderGroups: pipelinePlaceholderGroups } = usePipelineData();

  const revisionPlaceholders = useMemo(
    () =>
      allInputs
        .filter(({ context }) => context === 'revision')
        .map(({ spec }) => ({ context: 'revision' as IPlaceholderContext, ...spec })),
    [allInputs]
  );

  // Filter out deviceProperty input types because they are already included in DEVICE_PROPERTY_PLACEHOLDERS.
  const devicePlaceholders = useMemo(
    () =>
      allInputs
        .filter(({ context, spec }) => context === 'device' && spec.type !== 'deviceProperty')
        .map(({ spec }) => ({ context: 'device' as IPlaceholderContext, ...spec })),
    [allInputs]
  );

  const groups = useMemo(() => {
    const groups = [
      { name: 'Polia', placeholders: revisionPlaceholders },
      { name: 'Zariadenie', placeholders: [...DEVICE_PROPERTY_PLACEHOLDERS, ...devicePlaceholders] },
    ];

    if (!excludeSpecialTechnicianProperties) {
      groups.push({ name: 'Vlastnosť revízneho technika', placeholders: TECHNICIAN_PROPERTY_PLACEHOLDERS });
    }

    if (!excludeSpecialApproverProperties) {
      groups.push({ name: 'Vlastnosť schvaľovateľa', placeholders: APPROVER_PROPERTY_PLACEHOLDERS });
    }

    if (!excludeSpecialConfirmerProperties) {
      groups.push({ name: 'Vlastnosť preberateľa', placeholders: CONFIRMER_PROPERTY_PLACEHOLDERS });
    }

    groups.push(...pipelinePlaceholderGroups);

    if (!excludeSpecialRevisionProperties) {
      groups.push({
        name: 'Vlastnosť revíznej správy',
        placeholders: [...REVISION_PROPERTY_PLACEHOLDERS, ...departmentPlaceholders],
      });
    }

    if (!excludeSpecialOrganizationProperties) {
      groups.push({ name: 'Vlastnosť organizácie', placeholders: ORGANIZATION_PROPERTY_PLACEHOLDERS });
    }

    if (!excludeSpecialTechnicianOrganizationProperties) {
      groups.push({ name: 'Vlastnosť organizácie RT', placeholders: TECHNICIAN_ORGANIZATION_PROPERTY_PLACEHOLDERS });
    }

    if (!excludeSpecialAssignerOrganizationProperties) {
      groups.push({
        name: 'Vlastnosť organizácie zadávateľa',
        placeholders: ASSIGNER_ORGANIZATION_PROPERTY_PLACEHOLDERS,
      });
    }

    if (!excludeSpecialDepartmentProperties) {
      groups.push({
        name: departmentText('Vlastnosť položky úrovne', 'Vlastnosť strediska'),
        placeholders: DEPARTMENT_PROPERTY_PLACEHOLDERS,
      });
    }

    if (!excludeSpecialDocumentProperties) {
      groups.push({ name: 'Vlastnosť dokumentu', placeholders: DOCUMENT_PROPERTY_PLACEHOLDERS });
    }

    return groups;
  }, [
    departmentText,
    departmentPlaceholders,
    revisionPlaceholders,
    devicePlaceholders,
    pipelinePlaceholderGroups,
    excludeSpecialTechnicianProperties,
    excludeSpecialRevisionProperties,
    excludeSpecialOrganizationProperties,
    excludeSpecialTechnicianOrganizationProperties,
    excludeSpecialAssignerOrganizationProperties,
    excludeSpecialDepartmentProperties,
    excludeSpecialDocumentProperties,
  ]);

  return (
    <ScrollArea h={400}>
      {groups
        .filter(({ placeholders }) => placeholders.length > 0)
        .map(({ name, placeholders }, i) => (
          <Fragment key={name}>
            {i > 0 && <Menu.Divider my={8} />}
            {placeholders.map((placeholder) => (
              <Menu.Item
                key={placeholder.name}
                onClick={() => onClick(placeholder)}
                rightSection={
                  <P4Medium pl={20} c="gray.5">
                    {getPlaceholderTypeLabel(placeholder.type)}
                  </P4Medium>
                }
              >
                {placeholder.type === 'revisionProperty' && placeholder.name === '_department'
                  ? departmentText('Položka úrovne', 'Stredisko')
                  : placeholder.type === 'revisionProperty' && placeholder.name === '_departmentPath'
                  ? departmentText('Celá cesta položky úrovne', 'Celá cesta strediska')
                  : placeholder.label}
              </Menu.Item>
            ))}
          </Fragment>
        ))}
    </ScrollArea>
  );
}
